import { PlotlyFigure } from 'interfaces/agentSession';
import { ExperimentResult, Features } from 'interfaces/experimentResults';
import { stringify } from 'qs';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';

export interface NormalizationOverride {
  labId: string;
  studyId?: string;
  name: string;
  description?: string;
  channelOverrides: { [key: string]: [min: number, max: number] };
}

export interface ChannelNormalizationValueModel {
  markerType?: string | null;
  bitDepth?: 8 | 16 | null;
  channelNormalizationResults: { [key: string]: any } | null;
  wasAutoNormalized?: boolean | null;
}

export interface SlideChannelNormalizationResultSummary {
  slideId: string;
  orchestrationId?: string;
  experimentResultId: number;
  studyId: string;
  normalizationsSummary: { [key: string]: ChannelNormalizationValueModel };
}

export interface SlideChannelNormalizationExportRow {
  slideId: string;
  slideLabel: string;
  channelKey: string;
  markerType: string | null;
  minValue: number | null;
  maxValue: number | null;
  sourceStudyId: string | null;
  experimentResultId: number | null;
}

export interface PeakDetectionNormalizationRangeGroup {
  normalizationRange: [min: number, max: number];
  slideIds: string[];
}

export interface PeakDetectionMarkerNormalizationSummary {
  markerType: string;
  rangeGroups: PeakDetectionNormalizationRangeGroup[];
  missingSlideIds: string[];
}

export interface MarkerNormalizationObservation {
  markerId: string;
  channelKey: string;
  slideId: string;
  experimentResultId: number;
  minValue: number;
  maxValue: number;
  dynamicRange: number;
  minZScore: number;
  maxZScore: number;
  rangeZScore: number;
  absoluteMaxZScore: number;
  isOutlier: boolean;
}

export interface MarkerNormalizationSummary {
  markerId: string;
  slideCount: number;
  minCurrentValue: number;
  maxCurrentValue: number;
  dynamicRangeMin: number;
  dynamicRangeMax: number;
  dynamicRangeMean: number;
  dynamicRangeVariance: number;
  dynamicRangeStdDev: number;
  lowerFence: number;
  upperFence: number;
  outlierCount: number;
}

export interface SlideNormalizationSummary {
  slideId: string;
  markerCount: number;
  outlierMarkerCount: number;
  dominantMinMarkerId: string;
  dominantMinZScore: number;
  dominantMaxMarkerId: string;
  dominantMaxZScore: number;
  dominantRangeMarkerId: string;
  dominantRangeZScore: number;
  maxAbsoluteZScore: number;
}

export interface MarkerNormalizationPlotBundleResponse {
  markerSummaries: MarkerNormalizationSummary[];
  slideSummaries: SlideNormalizationSummary[];
  selectedMarkerId?: string | null;
  selectedMarkerSummary?: MarkerNormalizationSummary | null;
  selectedMarkerObservations: MarkerNormalizationObservation[];
  multichannelRangeFigure?: PlotlyFigure | null;
  multichannelZScoreFigure?: PlotlyFigure | null;
  selectedMarkerRangeFigure?: PlotlyFigure | null;
  selectedMarkerZScoreFigure?: PlotlyFigure | null;
}

export interface HistogramFlavorPreview {
  nonzeroBinIndices: number[];
  nonzeroBinCounts?: number[];
  nonzeroBinWeights?: number[];
  previewBinCount: number;
  totalCount: number;
  sourceBinCount: number;
}

export interface ChannelHistogramPreview {
  resultType?: string | null;
  bitDepth?: 8 | 16 | null;
  availableFlavors: string[];
  flavors?: Record<string, HistogramFlavorPreview | null> | null;
  raw?: HistogramFlavorPreview | null;
  nsm?: HistogramFlavorPreview | null;
}

export interface SlideChannelHistogramPreviewResponse {
  slideId: string;
  channelKey: string;
  channelIndex: number;
  markerType?: string | null;
  histogramPreview?: ChannelHistogramPreview | null;
}

export const getSlideChannelNormalizationsQueryKey = ({
  id,
  labId,
  studyId,
}: {
  id: string;
  labId: string;
  studyId: string;
}) => ['normalizations', { slideId: id, labId, studyId }];
export const getSlideChannelNormalizations = (
  { id, labId, studyId }: { id: string; labId: string; studyId: string },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<ExperimentResult[]>({
    url: `slides/${id}/multiplex/channel_normalizations?${stringify({ labId, studyId })}`,
    method: 'GET',
    signal,
  });
};

export const getNormalizationSummary = (casesParams: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<SlideChannelNormalizationResultSummary[]>({
    url: `slides/multiplex/channel_normalizations.get`,
    method: 'POST',
    data: JSON.stringify(casesParams),
    signal,
  });
};

export const getNormalizationExportRows = (casesParams: CasesParams, signal?: AbortSignal) =>
  apiRequestHandlerPromise<SlideChannelNormalizationExportRow[]>({
    url: 'slides/multiplex/channel_normalizations/export.get',
    method: 'POST',
    data: JSON.stringify(casesParams),
    signal,
  });

export const getPeakDetectionNormalizationSummary = (
  {
    casesParams,
    channelNames,
    normalizationName,
  }: {
    casesParams: CasesParams;
    channelNames: string[];
    normalizationName?: string | null;
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<PeakDetectionMarkerNormalizationSummary[]>({
    url: `slides/multiplex/peak_detection_normalizations.get`,
    method: 'POST',
    data: JSON.stringify({
      ...casesParams,
      channelNames,
      ...(normalizationName ? { normalizationName } : {}),
    }),
    signal,
  });
};

export interface BulkNormalizationOverride {
  casesParams: CasesParams;
  labId: string;
  markerRanges: Record<string, [min: number, max: number]>;
  name: string;
  description?: string;
}

export const generateBulkNormalizationOverrides = ({
  casesParams,
  ...bulkNormalizationOverride
}: BulkNormalizationOverride) => {
  return apiRequestHandlerPromise<ExperimentResult[]>({
    url: `slides/multiplex/channel_normalizations/bulk`,
    method: 'POST',
    data: JSON.stringify({ ...casesParams, ...bulkNormalizationOverride }),
  });
};

export type HistogramMaskType = 'full' | 'tsm' | 'roi';

export interface SlideHistogramAvailability {
  slideId: string;
  hasHistogram: boolean;
  maskType?: HistogramMaskType | null;
  isBounded: boolean;
  roiAssignmentId?: number | null;
  resolutionUmpp?: number | null;
}

export const getHistogramAvailabilityQueryKey = (casesParams: CasesParams) => ['histogramAvailability', casesParams];

export const getHistogramAvailability = (casesParams: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<SlideHistogramAvailability[]>({
    url: `slides/multiplex/histogram_availability.get`,
    method: 'POST',
    data: JSON.stringify(casesParams),
    signal,
  });
};

export const getNormalizationPlotBundle = (
  {
    casesParams,
    markerIds,
    selectedMarkerId,
  }: {
    casesParams: CasesParams;
    markerIds?: string[];
    selectedMarkerId?: string | null;
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<MarkerNormalizationPlotBundleResponse>({
    url: `slides/multiplex/channel_normalization_plots.get`,
    method: 'POST',
    data: JSON.stringify({
      ...casesParams,
      markerIds,
      selectedMarkerId,
    }),
    signal,
  });
};

export const generateNormalizationOverride = ({
  slideId,
  baseExperimentResultId,
  ...normalizationOverrideInput
}: { slideId: string; baseExperimentResultId: number } & NormalizationOverride) => {
  return apiRequestHandlerPromise<ExperimentResult>({
    url: `slides/${slideId}/multiplex/channel_normalizations/${baseExperimentResultId}`,
    method: 'POST',
    data: JSON.stringify(normalizationOverrideInput),
  });
};

export const generateManualNormalization = ({
  slideId,
  ...manualNormalizationInput
}: { slideId: string } & NormalizationOverride) => {
  return apiRequestHandlerPromise<ExperimentResult>({
    url: `slides/${slideId}/multiplex/channel_normalizations`,
    method: 'POST',
    data: JSON.stringify(manualNormalizationInput),
  });
};

export const getNormalizationValuesQueryKey = (slideId: string, experimentResultId: number) => [
  'normalizationValues',
  { slideId, experimentResultId },
];

export const getSlideChannelHistogramPreviewQueryKey = ({
  slideId,
  channelKey,
  previewBinCount,
}: {
  slideId: string;
  channelKey: string;
  previewBinCount: number;
}) => ['slideChannelHistogramPreview', { slideId, channelKey, previewBinCount }];

export const getSlideChannelHistogramPreview = (
  {
    slideId,
    channelKey,
    previewBinCount,
  }: {
    slideId: string;
    channelKey: string;
    previewBinCount: number;
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<SlideChannelHistogramPreviewResponse>({
    url: `slides/${slideId}/multiplex/channel_histograms/preview?${stringify({
      channelKey,
      previewBinCount,
    })}`,
    method: 'GET',
    signal,
  });
};

export const getNormalizationValues = <FeatureType = Features>(
  slideId: string,
  experimentResultId: number,
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<FeatureType>({
    url: `slides/${slideId}/multiplex/channel_normalizations/${experimentResultId}`,
    method: 'GET',
    signal,
  });
};
